import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import FileDownload from "js-file-download";
import { Grid, makeStyles, Box } from "@material-ui/core";

import { header } from "../../sample";
import { Portfolio } from "./Portfolio";
import { uploadFile } from "../../../../portfolio";
import { EXCEL_FILE_TYPE } from "../../../../../constants";
import { api } from "../../../../../services";
import { ShaDowBox } from "../../../../../components/boxes";
import SettingHeader from "../../../../../components/SettingHeader";
import { Button, UploadButton } from "../../../../../components/base";
import { PortfolioTable } from "../../../../../components/portfolioTable";
import { saveNotification } from "../../../../../redux/notification/actions";
import { SettingActions } from "../../../../../components/SettingHeader/settingActions";

export const useStyles = makeStyles(() => ({
  headers: {
    marginLeft: 50,
    paddingTop: 40,
    marginRight: 50,
  },
  saveChanges: {
    margin: "8px 14px 8px 0",
  },
}));

export const PortfolioRegistration = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [rowsErrors, setRowsErrors] = useState([]);
  const rowsRef = useRef();
  rowsRef.current = rows;
  const [uploading, setUploading] = useState(false);
  const [uploadingData, setUploadingData] = useState(false);
  const [portfolioCardData, setPortfolioCardData] = useState([]);
  const [nextItemEnable, setNextItemEnable] = useState(true);
  const [prevItemEnable, setPrevItemEnable] = useState(false);
  const [paginate, setPaginate] = useState({
    start: 0,
    end: 3,
  });
  const [draft, setDraft] = useState();
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    api
      .get("/onboarding/settings/portfolio-registration/draft/")
      .then((resp) => resp.data)
      .then((draft) => {
        setDraft(draft);
        if (draft.buildings) setRows(draft.buildings);
        setDocuments(draft.files);
        if (draft.files.length > 3) {
          const sets = draft.files.slice(paginate.start, paginate.end);
          setPortfolioCardData(sets);
        } else {
          setPortfolioCardData(draft.files);
          setNextItemEnable(false);
        }
      });
  }, []);

  const handlePaginate = () => {
    setPaginate({ start: paginate.start + 1, end: paginate.end + 1 });
    const sets = documents.slice(paginate.start + 1, paginate.end + 1);
    setPortfolioCardData(sets);

    if (documents.length === paginate.end + 1) {
      setNextItemEnable(false);
    }
    setPrevItemEnable(true);
  };

  const handleBackPaginate = () => {
    setPaginate({ start: paginate.start - 1, end: paginate.end - 1 });
    const sets = documents.slice(paginate.start - 1, paginate.end - 1);
    setPortfolioCardData(sets);

    if (paginate.start - 1 === 0) {
      setPrevItemEnable(false);
    }
    setNextItemEnable(true);
  };

  useEffect(() => {
    if (draft && draft?.is_table_enabled && !uploadingData) {
      const id = setInterval(() => {
        dispatch(
          saveNotification({
            notification: "Saving...",
          })
        );

        api
          .post(`/onboarding/settings/portfolio-registration/draft/`, rowsRef.current)
          .then((res) => {
            setRowsErrors([]);
            dispatch(
              saveNotification({
                notification: "Draft was successfully saved",
              })
            );
            return res.data;
          })
          .then(({ buildings }) => setRows(buildings))
          .catch((e) => {
            dispatch(
              saveNotification({
                notification: e?.response?.data?.message || "Oops...Something went wrong",
                error: true,
              })
            );
            if (Array.isArray(e?.response?.data)) {
              setRowsErrors(e.response.data);
            }
          })
          .finally(() => {
            dispatch(saveNotification({ notification: "" }));
          });
      }, 60000);
      return () => clearInterval(id);
    }
  }, [dispatch, draft, uploadingData]);

  const uploadXls = (event) => {
    setUploading(true);
    uploadFile(dispatch, event, (doc) => {
      if (doc?.buildings) setRows(doc.buildings);
      const tempDocuments = [...documents];
      tempDocuments.push(doc);
      setDocuments(tempDocuments);
      if (tempDocuments.length > 3) {
        setPaginate({
          start: tempDocuments.length - 3,
          end: tempDocuments.length,
        });
        const sets = tempDocuments.slice(paginate.start - 3);
        setPortfolioCardData(sets);
        setPrevItemEnable(true);
        setNextItemEnable(false);
      } else {
        setPortfolioCardData(tempDocuments);
        setNextItemEnable(false);
      }
    }).finally(() => {
      setUploading(false);
    });
  };

  const saveChanges = () => {
    setUploadingData(true);
    api
      .post(`/onboarding/settings/portfolio-registration/draft/save/`, rowsRef.current)
      .then((resp) => resp.data)
      .then((draft) => {
        setRows(draft.buildings);
        setRowsErrors([]);
      })
      .catch((e) => {
        let message = "Oops...Something went wrong.";
        if (e.response && "error" in e.response.data) {
          message = e.response.data.error;
        } else if (e.response.data instanceof Array) {
          setRowsErrors(e.response.data);
          message = "Some fields are required";
        }
        dispatch(
          saveNotification({
            message: message,
            error: true,
          })
        );
      })
      .finally(() => {
        setUploadingData(false);
      });
  };

  const downloadFile = (doc) => {
    api
      .get(`/onboarding/settings/portfolio-registration/draft/files/${doc.id}/download/`, {
        responseType: "blob",
      })
      .then((response) => {
        FileDownload(response.data, doc.name);
      })
      .catch(() => {
        dispatch(
          saveNotification({
            message: "Oops...Something went wrong.",
            error: true,
          })
        );
      });
  };

  const deleteBuilding = (id) => {
    api.delete(`/onboarding/settings/portfolio-registration/draft/buildings/${id}/`).catch(() => {
      dispatch(
        saveNotification({
          message: "Oops...Something went wrong.",
          error: true,
        })
      );
    });
  };

  return (
    <>
      <SettingActions />
      <ShaDowBox style={{ height: "100%" }}>
        <Box className={classes.headers}>
          <SettingHeader
            title={header.portfolioregistration.title}
            subtitle={header.portfolioregistration.subtitle}
            img={header.portfolioregistration.img}
          />
          <Grid container direction="row" justifyContent="flex-end">
            <Button
              text="SAVE CHANGES"
              disabled={!draft?.is_table_enabled}
              onClick={saveChanges}
              loading={uploadingData}
              rootStyle={classes.saveChanges}
            />
            <UploadButton
              accept={EXCEL_FILE_TYPE}
              loading={uploading}
              onChange={uploadXls}
              text="UPLOAD NEW VERSION"
              disabled={!draft?.is_table_enabled}
            />
          </Grid>
          {!!portfolioCardData.length && (
            <Portfolio
              portfolioCardData={portfolioCardData}
              nextItemEnable={nextItemEnable}
              prevItemEnable={prevItemEnable}
              handleBackPaginate={handleBackPaginate}
              handlePaginate={handlePaginate}
              handleClick={downloadFile}
            />
          )}
          <PortfolioTable
            rows={rows}
            setRows={setRows}
            disableTable={!draft?.is_table_enabled}
            rowsErrors={rowsErrors}
            deleteBuilding={deleteBuilding}
          />
        </Box>
      </ShaDowBox>
    </>
  );
};
